import { useEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useSearch } from '../api/hooks'
import type { SearchGroup } from '../api/models'
import { Badge, EmptyState, ErrorState, Spinner, formatAmount, formatDate } from '../components/ui'

const DEBOUNCE_MS = 220

/**
 * Render a ts_headline snippet.
 *
 * The snippet is built by Postgres from the raw document text, which came from a mail
 * and is not HTML-escaped. Instead of trusting it, the only markup it is allowed to
 * carry - the <mark> pairs - is parsed out and everything else is rendered as text, so
 * no document content can ever become markup.
 */
export function Highlight({ snippet }: { snippet: string }) {
  const parts = snippet.split(/(<mark>|<\/mark>)/g)
  let marked = false
  return (
    <>
      {parts.map((part, index) => {
        if (part === '<mark>') {
          marked = true
          return null
        }
        if (part === '</mark>') {
          marked = false
          return null
        }
        if (!part) return null
        return marked ? <mark key={index}>{part}</mark> : <span key={index}>{part}</span>
      })}
    </>
  )
}

/**
 * One field, natural language. The detected filters are shown as chips so the parse is
 * never a black box, and results are grouped by case.
 */
export function SearchPage() {
  const [input, setInput] = useState('')
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      setCursor(null)
      setQuery(input.trim())
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [input])

  const result = useSearch(query, cursor, query.length > 0)
  const groups = (result.data?.groups ?? []) as unknown as SearchGroup[]
  const parsed = result.data?.parsed_query

  return (
    <>
      <h1 className="mb-4 text-2xl">Suche</h1>

      <form role="search" className="mb-4" onSubmit={(event) => event.preventDefault()}>
        <label className="label" htmlFor="search-input">
          Wonach suchen Sie?
        </label>
        <input
          ref={inputRef}
          id="search-input"
          className="input text-base"
          type="search"
          placeholder="z. B. Rechnung Amazon USB-C Adapter"
          value={input}
          autoComplete="off"
          onChange={(event) => setInput(event.target.value)}
        />
        <p className="hint">
          Natürliche Sprache. Datum, Betrag, Typ und Absender werden automatisch erkannt.
        </p>
      </form>

      {parsed ? (
        <ul className="mb-4 flex flex-wrap gap-2" aria-label="Erkannte Filter">
          {parsed.doc_types?.map((type) => (
            <li key={type} className="chip">
              Typ: {type}
            </li>
          ))}
          {parsed.parties?.map((party) => (
            <li key={party} className="chip">
              Absender: {party}
            </li>
          ))}
          {parsed.date_from ? <li className="chip">ab {formatDate(parsed.date_from)}</li> : null}
          {parsed.date_to ? <li className="chip">bis {formatDate(parsed.date_to)}</li> : null}
          {parsed.identifiers?.map((identifier) => (
            <li key={identifier} className="chip font-mono">
              {identifier}
            </li>
          ))}
          {result.data?.used_llm ? <li className="chip">KI-Parsing</li> : null}
          {result.data ? (
            <li className="chip text-paper-500">
              {result.data.total} Treffer · {result.data.took_ms} ms
            </li>
          ) : null}
        </ul>
      ) : null}

      {result.isFetching ? <Spinner label="Wird gesucht" /> : null}
      {result.isError ? <ErrorState error={result.error} onRetry={() => void result.refetch()} /> : null}

      {query && !result.isFetching && groups.length === 0 && !result.isError ? (
        <EmptyState
          title="Keine Treffer"
          description="Andere Begriffe versuchen oder Filter weglassen. Die Suche findet auch Positionen und Rechnungsnummern."
        />
      ) : null}

      <ol className="space-y-3">
        {groups.map((group) => (
          <li key={group.case_id ?? group.documents[0]?.id} className="card">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {group.case_id ? (
                <Link
                  to="/vorgaenge/$caseId"
                  params={{ caseId: group.case_id }}
                  className="font-medium hover:underline"
                >
                  {group.title}
                </Link>
              ) : (
                <span className="font-medium">{group.title}</span>
              )}
              {group.status ? (
                <Badge tone={group.status === 'open' ? 'accent' : 'ok'}>
                  {group.status === 'open' ? 'offen' : 'erledigt'}
                </Badge>
              ) : null}
              <span className="ml-auto text-xs text-paper-400">
                {group.documents.length} Treffer im Vorgang
              </span>
            </div>

            <ul className="space-y-2">
              {group.documents.map((hit) => (
                <li key={hit.id} className="border-l-2 border-paper-200 pl-3 dark:border-paper-800">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to="/dokumente/$documentId"
                      params={{ documentId: hit.id }}
                      className="min-w-0 flex-1 truncate text-sm font-medium hover:underline"
                    >
                      {hit.title}
                    </Link>
                    {hit.doc_type ? <Badge tone="accent">{hit.doc_type}</Badge> : null}
                  </div>
                  {hit.highlight ? (
                    <p className="mt-1 text-sm text-paper-600 dark:text-paper-300">
                      <Highlight snippet={hit.highlight} />
                    </p>
                  ) : (
                    <p className="mt-1 text-sm text-paper-600 dark:text-paper-300">{hit.summary}</p>
                  )}
                  <p className="mt-1 text-xs text-paper-500">
                    {formatDate(hit.document_date)}
                    {hit.amount_total !== null ? ` · ${formatAmount(hit.amount_total, hit.currency)}` : ''}
                    {hit.party ? ` · ${hit.party}` : ''}
                    <span className="ml-2 text-paper-400">gefunden über {hit.matched_by.join(', ')}</span>
                  </p>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>

      {result.data?.next_cursor ? (
        <button
          type="button"
          className="btn-secondary mt-4"
          onClick={() => setCursor(result.data?.next_cursor ?? null)}
        >
          Weitere Treffer
        </button>
      ) : null}
    </>
  )
}
